/**
 * @fileoverview Custom hook for managing metadata form state and logic.
 * Encapsulates form state, dropdown data retrieval, and event handlers.
 */

import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "@/i18n";
import type { DropdownItem, MetadataItem } from "../types";
import {
  CATEGORY_TO_DROPDOWN_KEY,
  CATEGORY_TO_LABEL_KEY,
  CATEGORY_TO_NAME_FIELD,
} from "../types";

/**
 * Props for the useMetadataForm hook.
 */
interface UseMetadataFormProps {
  /** Internal category key (e.g., "departments", "roles") */
  readonly categoryKey: string;
  /** Display title of the metadata category */
  readonly categoryTitle: string;
  /** The metadata item being edited */
  readonly item: MetadataItem;
  /** Index of the item in the form array */
  readonly itemIndex: number;
  /** Whether the modal is open */
  readonly isOpen: boolean;
  /** Callback to close the modal */
  readonly onClose: () => void;
}

/**
 * Return type for the useMetadataForm hook.
 */
interface UseMetadataFormReturn {
  /** Whether this is a new item being added */
  isNewItem: boolean;
  /** German label for the category */
  germanCategoryTitle: string;
  /** Local state for the name field */
  localName: string;
  /** Setter for local name */
  setLocalName: (name: string) => void;
  /** Local state for the description field */
  localDescription: string;
  /** Setter for local description */
  setLocalDescription: (description: string) => void;
  /** Currently linked item ID */
  linkedId: number | undefined;
  /** Setter for linked ID */
  setLinkedId: (id: number | undefined) => void;
  /** Currently selected parent ID */
  parentId: number | undefined;
  /** Setter for parent ID */
  setParentId: (id: number | undefined) => void;
  /** Whether the link dropdown is visible */
  showDropdown: boolean;
  /** Setter for link dropdown visibility */
  setShowDropdown: (show: boolean) => void;
  /** Whether the parent dropdown is visible */
  showParentDropdown: boolean;
  /** Setter for parent dropdown visibility */
  setShowParentDropdown: (show: boolean) => void;
  /** Available items for linking */
  availableItems: DropdownItem[];
  /** Available parent items */
  parentItems: DropdownItem[];
  /** Name field to use from dropdown items */
  nameField: string;
  /** Label for the parent dropdown */
  parentLabel: string;
  /** Extracted name from document */
  extractedName: string | undefined;
  /** Handler for saving the form */
  handleSave: () => void;
  /** Handler for linking to an existing item */
  handleLinkToExisting: (dropdownItem: DropdownItem) => void;
  /** Handler for unlinking */
  handleUnlink: () => void;
  /** Handler for canceling */
  handleCancel: () => void;
}

/**
 * Custom hook for managing metadata form state and business logic.
 * Handles local state, dropdown data, and form interactions.
 *
 * @param props - Hook configuration
 * @returns Form state and handlers
 */
export function useMetadataForm({
  categoryKey,
  categoryTitle,
  item,
  itemIndex,
  isOpen,
  onClose,
}: UseMetadataFormProps): UseMetadataFormReturn {
  const { setValue, getValues } = useFormContext();
  const { t } = useTranslation();
  const isNewItem = !item.name;

  // Get translated label for the category
  const labelKey = CATEGORY_TO_LABEL_KEY[categoryKey];
  const germanCategoryTitle = labelKey ? t(labelKey) : categoryTitle;

  // Local state for form fields
  const [localName, setLocalName] = useState(item.name || item.title);
  const [localDescription, setLocalDescription] = useState(
    item.description || ""
  );
  const [linkedId, setLinkedId] = useState<number | undefined>(item.linkedId);
  const [parentId, setParentId] = useState<number | undefined>(item.parentId);
  const [extractedName, setExtractedName] = useState<string | undefined>(
    item.extracted_name
  );
  const [showDropdown, setShowDropdown] = useState(false);
  const [showParentDropdown, setShowParentDropdown] = useState(false);

  // Read dropdown data from localStorage
  const dropdownDataStr = localStorage.getItem("aiio_dropdown_data");
  const dropdownData = dropdownDataStr ? JSON.parse(dropdownDataStr) : null;

  // Get available items for this category
  const dropdownKey = CATEGORY_TO_DROPDOWN_KEY[categoryKey];
  const nameField = CATEGORY_TO_NAME_FIELD[categoryKey];
  const availableItems: DropdownItem[] =
    dropdownData?.data?.[dropdownKey] || [];

  // Get available parent items based on category
  let parentItems: DropdownItem[] = [];
  let parentLabel = "";

  if (categoryKey === "resources") {
    parentItems = dropdownData?.data?.resourceGroups || [];
    parentLabel = t("documentReview.modal.parentResourceGroup");
  } else if (categoryKey === "departments" || categoryKey === "roles") {
    parentItems = (dropdownData?.data?.orgCharts || []).filter(
      (chart: any) => chart.id != null
    );
    parentLabel = t("documentReview.modal.orgChart");
  } else if (categoryKey === "requirements") {
    parentItems = dropdownData?.data?.requirements || [];
    parentLabel = t("documentReview.modal.parentRequirement");
  }

  // Initialize local state when modal opens
  useEffect(() => {
    if (isOpen && item) {
      setLocalName(item.name);
      setLocalDescription(item.description || "");
      setLinkedId(item.linkedId);
      setParentId(item.parentId);
      setExtractedName(item.extracted_name);
    }
  }, [isOpen, item]);

  /**
   * Saves the current form state to the parent form context.
   * Either adds a new item or updates an existing one.
   */
  const handleSave = () => {
    // If it's a new item and name is empty, don't add it
    if (isNewItem && !localName?.trim()) {
      onClose();
      return;
    }

    const currentItems = getValues(categoryKey) || [];

    if (isNewItem) {
      // Add new item to the array
      const newItem = {
        id: item.id,
        name: localName,
        description: localDescription,
        linkedId,
        parentId,
        extracted_name: extractedName,
      };
      setValue(categoryKey, [...currentItems, newItem]);
    } else {
      // Update existing item
      const updatedItems = [...currentItems];
      updatedItems[itemIndex] = {
        ...updatedItems[itemIndex],
        name: localName,
        description: localDescription,
        linkedId,
        parentId,
        extracted_name: extractedName,
      };
      setValue(categoryKey, updatedItems);
    }

    onClose();
  };

  /**
   * Links the current item to an existing AIIO element.
   * Populates the name field and clears description/parent fields.
   * Saves the current name as extracted_name before overwriting.
   *
   * @param dropdownItem - The existing item to link to
   */
  const handleLinkToExisting = (dropdownItem: DropdownItem) => {
    const itemName =
      (dropdownItem[nameField as keyof DropdownItem] as string) || "";
    // Save current name as extracted_name if not already set
    if (!extractedName && localName) {
      setExtractedName(localName);
    }
    setLocalName(itemName);
    setLocalDescription(""); // Clear description as linked items don't need it
    setParentId(undefined); // Clear parent selection as linked items don't need it
    setLinkedId(dropdownItem.id);
    setShowDropdown(false);
  };

  /**
   * Removes the link to an existing AIIO element.
   * Restores the name from extracted_name and removes the linked ID.
   */
  const handleUnlink = () => {
    // Restore the extracted name when unlinking
    if (extractedName) {
      setLocalName(extractedName);
    }
    setLinkedId(undefined);
  };

  /**
   * Closes the modal without saving any changes.
   */
  const handleCancel = () => {
    onClose();
  };

  return {
    isNewItem,
    germanCategoryTitle,
    localName: localName || "",
    setLocalName,
    localDescription,
    setLocalDescription,
    linkedId,
    setLinkedId,
    parentId,
    setParentId,
    showDropdown,
    setShowDropdown,
    showParentDropdown,
    setShowParentDropdown,
    availableItems,
    parentItems,
    nameField,
    parentLabel,
    extractedName,
    handleSave,
    handleLinkToExisting,
    handleUnlink,
    handleCancel,
  };
}
